import { ChangeDetectionStrategy, Component, DestroyRef, Inject, OnInit, Optional, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ActionIconDef, Grid, GridActionIconClickEvent, GridConfig, GridHyperlinkClickEvent, GridToolbarActionClickEvent } from '@inavista/components/grid';
import { catchError, forkJoin, of } from 'rxjs';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { vrStatuses } from '../model/vacation-request.model';
import { VacationRequestService } from '../vacation-request.service';
import { SharedService } from 'projects/shared/src/lib/shared.service';
import { VacationRequestsPopupComponent } from './vacation-requests-popup/vacation-requests-popup.component';
import { ModuleNames, RolePermissionsModel, tabsModel } from '../../../models/lib.model';
import { UserRoles } from '../../../lib.constants';
import { APP_CONFIG, AppConfig } from '../../../app-config.token';
import {
  approveDisabledReason, CREATE_REQUEST_ACTION, deleteDisabledReason, REFRESH_ACTION, rejectDisabledReason, VacationRequestsGridContext,
  vacationRequestsActionIcons, vacationRequestsGridConfig, vacationRequestsToolbarActions,
} from './vacation-requests-grid.config';

// Out of Office Requests (/vacations-request in RIMS Sales, /home/vacations-request in RIMS Service) on
// @inavista/components: the rows come from RIMS Master api/grid/vacation-requests (vacation-requests-grid.config.ts). The ID
// and Details open the request popup; Approve, Reject and Delete call the request endpoints, as before.
@Component({
    selector: 'app-vacation-requests-grid',
    templateUrl: './vacation-requests-grid.component.html',
    styleUrls: ['./vacation-requests-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class VacationRequestsGridComponent implements OnInit {
  @ViewChild('ooGrid') grid?: Grid;

  private readonly destroyRef = inject(DestroyRef);

  PERMISSIONS: RolePermissionsModel[] = [];
  private ctx!: VacationRequestsGridContext;
  // Null until the permissions are known; then the grid, or the no-permission message.
  readonly gridConfig = signal<GridConfig | null>(null);
  readonly actionIcons = signal<ActionIconDef[]>([]);
  readonly noPermission = signal(false);
  private openedFromLink = false;

  constructor(
    public dialog: MatDialog,
    private readonly vrService: VacationRequestService,
    private readonly route: ActivatedRoute,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    @Optional() public dialogRef: MatDialogRef<VacationRequestsGridComponent>,
    @Inject(APP_CONFIG) private config: AppConfig,
    private readonly sharedService: SharedService
  ) {}

  ngOnInit() {
    this.loadPermissions();
    this.getSetModuleStatuses();
  }

  // The page's rules (outOfOfficeRequest_MainGrid) and the menu rule the API checks (menuItem_OutOfOfficeRequest): without
  // it the page says so, instead of a grid the API refuses.
  loadPermissions() {
    const page$ = this.sharedService.getRolePermission('outOfOfficeRequest', 'outOfOfficeRequest_MainGrid', '', '').pipe(catchError(() => of(null)));
    const menu$ = this.sharedService.getRolePermission('menuItem', '', '', '').pipe(catchError(() => of(null)));
    forkJoin([page$, menu$]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(([page, menu]: any[]) => {
      this.PERMISSIONS = page?.result ?? [];
      const menuItems: RolePermissionsModel[] = menu?.result ?? [];
      if (!menuItems.find(p => p.ruleAction === 'menuItem_OutOfOfficeRequest')?.visible) {
        this.noPermission.set(true);
        return;
      }
      const permission = (key: string) => ({ visible: !!this.getPermission(key)?.visible, enable: !!this.getPermission(key)?.enable });
      const excel = permission('mainGrid_excelExport');
      this.ctx = {
        masterApiUrl: this.config.masterApiURL ?? '',
        canExport: excel.visible && excel.enable && this.sharedService.getUSER()?.role !== UserRoles.salesRep,
        approve: permission('mainGrid_approveRequest'),
        reject: permission('mainGrid_rejectRequest'),
        delete: permission('mainGrid_deleteRequest'),
        create: permission('mainGrid_createRequest'),
      };
      this.actionIcons.set(vacationRequestsActionIcons(this.ctx));
      this.gridConfig.set({
        ...vacationRequestsGridConfig(this.ctx),
        toolbarActions: [REFRESH_ACTION, ...vacationRequestsToolbarActions(this.ctx)],
      });
      this.openFromLink();
    });
  }

  getPermission(ruleAction: string): RolePermissionsModel {
    return this.PERMISSIONS?.find((e: RolePermissionsModel) => e.ruleAction === ruleAction)!;
  }

  // vacations-request?primaryId=<id>&showComments=true (a comment notification) opens that request with its comments.
  private openFromLink() {
    if (this.openedFromLink) return;
    this.openedFromLink = true;
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const primaryId = params.get('primaryId');
      if (!primaryId || params.get('showComments') !== 'true') return;
      this.openVRPopup({ vrfId: primaryId }, tabsModel.details);
      setTimeout(() => this.showComments(primaryId), 500);
    });
  }

  private showComments(vrfId: string) {
    this.sharedService.globalComments = {
      ...this.sharedService.globalComments,
      grid: { refresh: () => this.refresh() } as any, module: ModuleNames.VacationRequest, moduleId: vrfId, showComments: true,
    };
  }

  private refresh() {
    this.grid?.refresh({ keepScroll: true });
  }

  onToolbarAction(event: GridToolbarActionClickEvent) {
    if (event.key === REFRESH_ACTION.key) {
      this.loadPermissions();
      this.refresh();
    } else if (event.key === CREATE_REQUEST_ACTION) {
      const user = this.sharedService.getUSER();
      this.openVRPopup({
        status: 'Open',
        statusCode: this.vrService.getVRStatus('Open'),
        canEdit: true,
        userId: user?.userId,
        vrfId: null,
        other: [],
        reporter: user?.firstName + ' ' + user?.lastName,
      });
    }
  }

  // Same file as before: "Vacation Requests (<date>).xlsx".
  onExportRequested() {
    if (this.grid) exportInavistaGrid(this.grid, 'Vacation Requests ', undefined, 'Vacation Requests');
  }

  onHyperlinkClick(event: GridHyperlinkClickEvent) {
    const row: any = event.row;
    this.openVRPopup({ ...row, statusCode: this.vrService.getVRStatus(row?.status) });
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    const row: any = { ...event.row };
    switch (event.iconKey) {
      case 'details':
        this.openVRPopup(row);
        break;
      case 'comments':
        // Opened after this click: the comments panel closes on any document click outside it.
        setTimeout(() => this.showComments(row.vrfId));
        break;
      case 'attachments':
        this.openVRPopup(row, tabsModel.attachments);
        break;
      case 'history':
        this.openVRPopup(row, tabsModel.history);
        break;
      case 'approve': {
        const reason = approveDisabledReason(row, this.ctx);
        if (reason) { this.sharedService.showNotification('info', reason); return; }
        this.sharedService.showConfirmation(`Are you sure to approve the request: ${row.vrfId}?`).then(res => {
          if (!res.isConfirmed) return;
          row.status = vrStatuses.approved;
          this.vrService.updateStatusTo(row).subscribe({
            next: (message: any) => {
              this.sharedService.showNotification('success', message);
              this.refresh();
            },
            error: (err: any) => this.sharedService.showNotification('error', err?.error?.message ?? err?.message ?? 'The request could not be approved.'),
          });
        });
        break;
      }
      case 'reject': {
        const reason = rejectDisabledReason(row, this.ctx);
        if (reason) { this.sharedService.showNotification('info', reason); return; }
        this.vrService.showCommentOnRejectionModal(row).then(() => this.refresh()).catch(() => undefined);
        break;
      }
      case 'delete': {
        const reason = deleteDisabledReason(row);
        if (reason) { this.sharedService.showNotification('info', reason); return; }
        this.sharedService.showConfirmation(`Are you sure to delete the request: ${row.vrfId}?`).then(res => {
          if (!res.isConfirmed) return;
          this.vrService.deleteRequest(row.vrfId).subscribe({
            next: (message: any) => {
              this.sharedService.showNotification('success', message);
              this.refresh();
            },
            error: (err: any) => this.sharedService.showNotification('error', err?.error?.message ?? err?.message ?? 'The request could not be deleted.'),
          });
        });
        break;
      }
    }
  }

  openVRPopup(data: any, tab: tabsModel = tabsModel.details) {
    const dialogRef = this.dialog.open(VacationRequestsPopupComponent, {
      disableClose: true,
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '100%',
      height: '100%',
      enterAnimationDuration: '0',
      exitAnimationDuration: '0',
      data: { ...data, tab },
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refresh());
  }

  getSetModuleStatuses() {
    this.vrService.getVacationRequestsStatus().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => this.vrService.setVRStatuses(res?.result),
    });
  }
}
